// PartnerBanner · single full-width CTA strip that mirrors the live
// ofinancial.markets partnership banner. Replaces the placeholder
// "Affiliates" link in the funnel.
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Users, Wallet, TrendingUp, ArrowRight } from "lucide-react";

const PARTNER_STATS = [
  { Icon: Users,      key: "active", label: "Active partners", value: "1,000+" },
  { Icon: Wallet,     key: "paid",   label: "Paid to partners", value: "$2.8M+" },
  { Icon: TrendingUp, key: "uplift", label: "Avg uplift",       value: "3.4×" },
];

export default function PartnerBanner() {
  const { t } = useTranslation();
  return (
    <section data-testid="partner-banner" className="relative py-20 lg:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-3xl border overflow-hidden p-8 sm:p-12 lg:p-16"
          style={{
            borderColor: "color-mix(in srgb, var(--brand) 30%, var(--line-strong))",
            background: "linear-gradient(135deg, color-mix(in srgb, var(--brand) 12%, var(--surface)), color-mix(in srgb, var(--surface-2) 60%, transparent))",
          }}
        >
          {/* Decorative glow */}
          <div aria-hidden className="absolute -top-32 -right-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 22%, transparent), transparent 60%)" }} />
          <div aria-hidden className="absolute -bottom-32 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, color-mix(in srgb, var(--gold-2) 14%, transparent), transparent 60%)" }} />

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="text-center lg:text-left">
              <div className="pill-mono inline-flex mb-5">
                <Users size={11} strokeWidth={2.2} className="mr-1.5" style={{ color: "var(--brand)" }} />
                {t("partner.pill", "Partnership Program")}
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-[-0.025em] leading-[1.05] text-text-1 mb-5">
                {t("partner.h1", "There has never been a better opportunity to")} <span className="text-gradient-brand font-semibold">{t("partner.hAccent", "refer traders.")}</span>
              </h2>
              <p className="text-text-2 text-[15px] sm:text-base leading-relaxed font-light max-w-xl mx-auto lg:mx-0 mb-7">
                {t("partner.sub", "Per-lot rebates and CPA options on every active referred client, plus a dedicated partner manager. Real-time dashboard and daily payouts.")}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 items-center sm:items-stretch justify-center lg:justify-start">
                <Link to="/partner" className="btn-solid-brand !h-12 !px-7" data-testid="partner-banner-cta-primary">
                  {t("partner.cta1", "Become a partner")} <ArrowRight size={16} />
                </Link>
                <a href="mailto:partners@ofinancial.markets?subject=Partnership%20inquiry" className="btn-ghost-rotating text-sm px-7" data-testid="partner-banner-cta-secondary">
                  {t("partner.cta2", "Contact partnership team")}
                </a>
              </div>
            </div>

            {/* Right · stats grid */}
            <div className="grid grid-cols-3 gap-3 sm:gap-4">
              {PARTNER_STATS.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.15 + i * 0.07 }}
                  className="text-center min-w-0"
                  data-testid={`partner-banner-stat-${i}`}
                >
                  <s.Icon size={22} strokeWidth={1.6} className="mx-auto mb-2.5" style={{ color: "var(--brand)" }} />
                  <div className="font-display text-2xl sm:text-3xl font-semibold text-text-1 tabular-nums leading-none">{s.value}</div>
                  <div className="text-text-3 mt-2 leading-tight text-[12px] sm:font-mono sm:text-[12px] sm:uppercase sm:tracking-[0.18em]">{t(`partner.stats.${s.key}`, s.label)}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
